
import React from 'react';
import { TRANSLATIONS } from '../../constants';
import { Language } from '../../types';

interface AuthFormContainerProps {
  isLoading: boolean;
  googleBtnRef: React.RefObject<HTMLDivElement>;
  isMobileFormVisible: boolean;
  setIsMobileFormVisible: (visible: boolean) => void;
  language: Language;
  onLahjaLogin: () => void; // This calls auth.signIn() from parent
}

const AuthFormContainer: React.FC<AuthFormContainerProps> = ({
  isLoading, googleBtnRef, isMobileFormVisible, setIsMobileFormVisible, language, onLahjaLogin
}) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';

  return (
    <div className={`w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative ${isMobileFormVisible ? 'flex animate-in slide-in-from-bottom-full duration-500' : 'hidden lg:flex'}`}>
       <button onClick={() => setIsMobileFormVisible(false)} className="lg:hidden absolute top-6 left-6 w-10 h-10 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center text-slate-500 z-20">
          <i className={`fas fa-arrow-${isRtl ? 'right' : 'left'}`}></i>
       </button>

       <div className="w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-8 duration-700">
          <div className="text-center mb-12">
             <div className="inline-flex w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-[28px] items-center justify-center shadow-2xl shadow-emerald-500/30 mb-6">
                <img src="logo.png" className="w-10 h-10 object-contain brightness-0 invert" alt="L" />
             </div>
             <h2 className="text-2xl font-black text-slate-800 dark:text-white mb-2">{isRtl ? 'تسجيل الدخول' : 'Welcome Back'}</h2>
             <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed px-4">
               {isRtl 
                 ? 'قم بتسجيل الدخول للمتابعة إلى منصة لهجة للذكاء الاصطناعي عبر الهوية الموحدة' 
                 : 'Sign in to continue to Lahja AI Platform via Secure SSO'}
             </p>
          </div>

          <div className="bg-white dark:bg-[#111115] rounded-[32px] p-8 lg:p-10 shadow-2xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-white/5 space-y-6">
             
             {/* OIDC Login Button */}
             <button 
                onClick={onLahjaLogin}
                disabled={isLoading}
                className={`
                   w-full relative group overflow-hidden bg-emerald-500 hover:bg-emerald-600 text-white font-black py-4 rounded-2xl 
                   shadow-xl shadow-emerald-500/20 transition-all duration-300 transform
                   hover:scale-[1.02] active:scale-[0.95] active:bg-emerald-700
                   focus:outline-none focus:ring-4 focus:ring-emerald-500/30
                   disabled:opacity-80 disabled:scale-100 disabled:cursor-wait
                   flex items-center justify-center gap-3
                `}
             >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                {isLoading ? (
                   <div className="flex items-center gap-2 animate-pulse">
                      <i className="fas fa-circle-notch fa-spin text-lg"></i>
                      <span>{isRtl ? 'جاري التحويل...' : 'Redirecting...'}</span>
                   </div>
                ) : (
                   <>
                      <img src="logo.png" className="w-5 h-5 object-contain brightness-0 invert group-hover:scale-110 transition-transform" alt="L" />
                      <span>{isRtl ? 'تسجيل الدخول عبر لهجة' : 'Log In with Lahja'}</span>
                   </>
                )}
             </button>

             <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
                <span className="flex-shrink-0 mx-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.orWith}</span>
                <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
             </div>

             {/* Google Button Container (Optional Fallback) */}
             <div ref={googleBtnRef} className="w-full flex justify-center min-h-[44px]"></div>
          </div>

          <p className="mt-8 text-center text-[10px] font-bold text-slate-400 dark:text-slate-600 opacity-60">
             {t.rights} &copy; 2025
          </p>
       </div>
    </div>
  );
};

export default AuthFormContainer;
